import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { always } from 'ramda';

import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { Button, Icons } from '@classity-edtech/ui-base';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import { Container, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import { ProgressStepper } from '@classity-edtech/ui-base/dist/components/Feedbacks/ProgressStepper/ProgressStepper';
import { CloseIcon } from '@classity-edtech/ui-base/dist/components/Displays';

export interface NavigationProps extends MuiBoxProps {
  goBack: () => void;
  continueAction?: () => void;
  closeAction?: () => void;
  continueLabel?: string;
  disableBack?: boolean;
  currentStepIndex: number;
  hideInfo?: boolean;
  steps?: { label: string; onClick: () => void }[];
}

export const Navigation = ({
  className,
  goBack,
  continueAction,
  closeAction,
  disableBack,
  currentStepIndex,
  hideInfo,
  steps: customSteps,
}: NavigationProps) => {
  const screens = useAppMediaQuery();
  const headerRef = useRef(null);
  const [isScrolledOut, setIsScrolledOut] = useState(false);

  const steps = customSteps ?? [
    { label: 'Booking', onClick: () => {} },
    { label: 'Checkout', onClick: () => {} },
    { label: 'Confirmation', onClick: () => {} },
  ];

  const mobileSteps =
    currentStepIndex < 1
      ? steps.slice(0, 3)
      : [
          steps.find((_, i) => i === currentStepIndex - 1),
          steps.find((_, i) => i === currentStepIndex),
          steps.find((_, i) => i === currentStepIndex + 1),
        ];

  const localisedMessage = (id: string) => {
    const F: any = FormattedMessage;
    return <F id={id} />;
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsScrolledOut(entry.intersectionRatio === 0);
      },
      {
        root: null,
        threshold: 0,
      },
    );

    if (headerRef.current) {
      observer.observe(headerRef.current);
    }

    return () => {
      if (headerRef.current) {
        observer.unobserve(headerRef.current);
      }
    };
  }, []);

  return (
    <>
      <Box
        className={className}
        sx={{
          transition: 'all 0.3s ease-in-out',
          top: 0,
          position: isScrolledOut && 'sticky',
          zIndex: 40,
          borderBottom:
            !isScrolledOut &&
            `solid 1px ${globalColours['$global-colour-interactive-secondary-default']}`,
          boxShadow: isScrolledOut && '0px 0px 40px 0px rgba(69, 71, 69, 0.20)',
          backgroundColor: globalColours['$global-colour-background-elevated'],
          mx: { xs: '-16px', md: 'auto' },
          px: { xs: '16px', md: '0' },
        }}
      >
        <Container
          sx={theme => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            pt: 3,
            pb: 3,
            px: 0,
            overflow: 'auto',
            width: '100%',
          })}
        >
          <Icons.ArrowRightIcon
            data-testid='navigation-back'
            sx={{
              cursor: !disableBack ? 'pointer' : 'not-allowed',
              mr: 'auto',
              transform: 'rotate(180deg)',
              color: !disableBack
                ? ''
                : globalColours['$global-colour-interactive-secondary-default'],
            }}
            onClick={!disableBack ? goBack : null}
          />
          <Box sx={{ width: { xs: '60%', md: '75%' }, mt: { xs: '', md: -7 } }}>
            {steps.length > 0 && (
              <ProgressStepper
                steps={screens.isMobile ? mobileSteps : steps}
                currentStepIndex={
                  !screens.isMobile
                    ? currentStepIndex
                    : currentStepIndex !== 0 && currentStepIndex !== steps?.length - 1
                      ? 1
                      : currentStepIndex
                }
                hideInfo={screens.isMobile || hideInfo}
              />
            )}
          </Box>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
            {continueAction && (
              <Button
                sx={{
                  py: 1.5,
                  px: 3,
                  height: screens.isMobile ? '64px' : '48px',
                  zIndex: 20,
                }}
                isLoading={false}
                onClick={continueAction}
                showRightIcon={screens.isMobile ? true : false}
              >
                <Typography variant='body3SemiBold' sx={{ textTransform: 'uppercase' }}>
                  {localisedMessage('continue')}
                </Typography>
              </Button>
            )}
            {closeAction && <CloseIcon sx={{ width: '16px', height: '16px' }} />}
          </Box>
        </Container>
      </Box>

      <Box ref={headerRef}></Box>
    </>
  );
};

Navigation.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  goBack: PropTypes.func,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
    }),
  ),
  continueAction: PropTypes.func,
  closeAction: PropTypes.func,
  disableBack: PropTypes.bool,
  currentStepIndex: PropTypes.number.isRequired,
  hideInfo: PropTypes.bool,
};

Navigation.defaultProps = {
  children: null,
  className: null,
  goBack: always,
  steps: [],
  continueAction: null,
  closeAction: null,
  disableBack: false,
  hideInfo: false,
};

export default styled<any>(Navigation)(
  ({ theme }) => `
    & .MuiPaper-root {
    }
  `,
);
